import { countryData } from '@consts/countryData';
import { parseDate, dateFormat } from './dates';
import dayjs from 'dayjs';
import weekOfYear from 'dayjs/plugin/weekOfYear';
import quarterOfYear from 'dayjs/plugin/quarterOfYear';
import { styleDate } from './styles';
import { setModels } from '@features/redux/slices/designSlice';
import { store } from '@features/redux/store';
import { MaterialIdMap } from '@consts/materials';

import type { REPORT_RANGE } from '@consts/reports';
import type { MoGlobalPresence } from '@/types/dashboard';
import type { Model } from '@features/redux/slices/designSlice';
import type { Cut, ParsedCut } from '@/types/cuts';
import type { CutType } from '@api/cut';
import type { Dictionary } from '@/types/general';
import type { IdMap, NameMap } from '@api/org';
import type { Option } from '../components/forms/dropdowns/base/FormMultiSelect';
import type { SelectedState } from '../components/forms/dropdowns/MultiOrgDropdowns';
import type { Dispatch, SetStateAction } from 'react';

dayjs.extend(weekOfYear);
dayjs.extend(quarterOfYear);

/**
 * Lodash polyfill.
 */
export function groupBy<T extends Record<string, any>>(items: T[], fn: (item: T) => string): Dictionary<T[]>;
export function groupBy<T extends Record<string, any>>(items: T[], key: keyof T): Dictionary<T[]>;
export function groupBy<T extends Record<string, any>>(items: T[], val: keyof T | ((item: T) => string)) {
    return items.reduce((acc, curr) => {
        const key = typeof val === 'function' ? val(curr) : curr[val];
        const arr = (acc[key] ||= []);
        arr.push(curr);

        return acc;
    }, {} as Dictionary<T[]>);
}

/**
 * Lodash polyfill.
 */
export function keyBy<T extends Record<string, any>>(items: T[], key: keyof T): Dictionary<T>;
export function keyBy<T extends Record<string, any>>(items: T[], fn: (item: T) => string): Dictionary<T>;
export function keyBy<T extends Record<string, any>>(items: T[], val: ((item: T) => string) | keyof T) {
    return items.reduce((acc, curr) => {
        const key = typeof val === 'function' ? val(curr) : curr[val];
        acc[key] = curr;

        return acc;
    }, {} as Dictionary<T>);
}

/**
 * Groups data (single-item) using 2 keys, specified by order of the keys array
 * In the future, an n-length keys array would be ideal
 */
export const groupByKeys = <T extends { [key: string]: string | number }, k extends keyof T>(
    data: T[],
    keys: [k, k]
) => {
    return data.reduce((acc: { [key1 in T[k]]?: { [key2 in T[k]]?: T } }, curr: T) => {
        const [startKey, endKey] = keys;

        const startProp = curr[startKey];
        const endProp = curr[endKey];

        if (!acc[startProp]) {
            acc[startProp] = {};
        }

        (acc[startProp] as any)[endProp] = curr;

        return acc;
    }, {});
};

export const lagDateRange = (dateRange: { startDate: string; endDate: string }, numMonths: number) => {
    const startDate = dayjs(dateRange.startDate);
    const newDate = startDate.month(startDate.get('month') - numMonths);
    return {
        startDate: dateFormat(newDate.toString()),
        endDate: dateRange.endDate,
    };
};

export const fixedDateRange = (dateRange: { startDate: string; endDate: string }) => {
    const startDate = dayjs(dateRange.startDate);
    const endDate = dayjs(dateRange.endDate);

    return {
        startDate: dateFormat(startDate.startOf('month').toString()),
        endDate: dateFormat(endDate.endOf('month').toString()),
    };
};

/**
 * This is used for when there is data to be grouped into monthly date periods.
 * From a set of dates and grouping size, this will yield a reference item
 * to reference any data global to that data grouping and data pulled from
 * the date grouping.
 */
export const createRowsFromGroupedDates = <T>(
    groupedData: {
        [key: string]: { [key: string]: T | undefined } | undefined;
    },
    dateKeys: { date: string; styled: string }[],
    groupingSize: number
) => {
    const sortedDates = [...dateKeys].map(({ date }) => date);

    return Object.values(groupedData).flatMap((data) => {
        return sortedDates.reduce((acc: (T | undefined)[][], date: string, index: number) => {
            if (index >= groupingSize) {
                // find non-null item in the data set in order to create a reference item
                const refItemDate = sortedDates.find((date) => data?.[date]) as string;
                const refItem = data![refItemDate] as T;

                /**
                 * reverse this date range since date-keys are passed in least-to-greatest
                 * slicing this data in order to achieve desired grouping
                 **/
                const dateRange = sortedDates.slice(index - groupingSize, index + 1).reverse();

                return [...acc, [{ ...refItem, date }, ...dateRange.map((date) => data?.[date])]];
            }

            return acc;
        }, []);
    });
};

export const sortDates = (dates: string[], format: REPORT_RANGE) => {
    return [...dates].sort((a, b) => parseDate(a, format).unix() - parseDate(b, format).unix());
};

export const getUniqueDates = (data: any, format: REPORT_RANGE) => {
    const dateSet = new Set(data.map((item: any) => item.date));
    const dateArr = Array.from(dateSet) as string[];
    const sorted = sortDates(dateArr, format);
    return sorted.map((date) => ({ date, styled: styleDate(format)(date) }));
};

export const getCountriesInfo = (data: MoGlobalPresence[]) => {
    return data.reduce((acc: { code3: string; name: string }[], item) => {
        const result = countryData.find((country) => country.country === item.country);

        if (result) {
            return [
                ...acc,
                {
                    code3: result.iso3,
                    name: result.country,
                },
            ];
        }

        return acc;
    }, []);
};

export const flattenByKey = <T, k extends string & keyof T>(arr: T[], key: k) => {
    return arr.map((obj) => obj[key]);
};

export const getDataByKey =
    <T, k extends keyof T>(dateKeys: { styled: string; date: string }[], groupedByDate: { [key: string]: T[] }) =>
    (key: k) => {
        return dateKeys.map(({ date }) => groupedByDate?.[date]?.[0]?.[key] || 0);
    };

export const getValues = <T, k extends keyof T>(items: (T | undefined)[], keys: k[]) => {
    return items.flatMap((item) => [...keys.map((key) => item?.[key] || 0)]);
};

export const seriesMap = <T extends Record<any, any>>(params: {
    items: T;
    dateKeys: { date: string; styled: string }[];
    valueKey: keyof T;
    visible: boolean;
    name?: (key: string) => string;
}) => {
    return Object.entries(params.items)
        .sort()
        .flatMap(([itemKey, item]) => ({
            visible: params.visible,
            name: params.name ? params.name(itemKey) : itemKey,
            data: params.dateKeys.map(({ date }) => item?.[date]?.[params.valueKey] || 0),
        }));
};

export const optimisticModelUpdate = (models: Model[], newModel: Model) => {
    const itemToRemove = models.findIndex((model) => model.modelId === newModel.modelId);
    const modelsRemoved =
        itemToRemove === -1 ? [...models] : [...models.slice(0, itemToRemove), ...models.slice(itemToRemove + 1)];
    const modelsAdded = [newModel, ...modelsRemoved];
    store.dispatch(setModels(modelsAdded));
};

export const transformCuts = (data: Cut[], companies: any, locations: any, resellers: any) => {
    return data.map((cut: Cut): ParsedCut => {
        const { materialId, companyID, resellerId, locationId, cutDate, syncDate, ...rest } = cut;

        return {
            ...rest,
            cutType: rest.cutType as CutType,
            cutDate: cutDate,
            syncDate: syncDate,
            material: (MaterialIdMap as any)[materialId],
            company: (companies as any)[companyID] || '-',
            locationId: `#${locationId}`,
            location: (locations as any)[locationId] || '-',
            reseller: resellers[resellerId] || companies[resellerId] || '-',
            batchId: cut.batchId ?? '',
            creditId: cut.creditId ?? '',
        };
    });
};

/** Filter out keys present in one object from the Other. */
export const differenceObjects = (lh: { [key: string]: any }, rh: { [key: string]: any }) => {
    return Object.entries(rh).reduce((acc, [k, v]) => {
        if (lh[k]) {
            return acc;
        }

        acc[k] = v;
        return acc;
    }, {} as { [key: string]: any });
};

/**
 * Convert `{ [ParentID: string]: ChildID[] }` to `{ [ParentID: string]: Option[] }`
 *
 * @param map The map of ParentID->ChildIDs to convert
 * @param names A map of ChildID->ChildName
 */
export const createOptionMap = (map: { [parentId: string]: number[] }, names: IdMap) => {
    return Object.entries(map).reduce((acc, [parentId, values]) => {
        acc[parentId] = values.reduce((options, id) => {
            if (id !== null && id !== undefined) {
                options.push({ id: id.toString(), name: names[id] });
            }
            return options;
        }, [] as Option[]);

        return acc;
    }, {} as { [parentId: string]: Option[] });
};

/**
 * Create a handler for a "dependent" selection options.
 *
 * @param target The state key corresponding to the options
 * @param reset An array of state keys to be reset (to `[]`) when the `target` is set
 */
export const createDropdownHandler =
    <T extends keyof SelectedState>(target: T, reset: Exclude<keyof SelectedState, T>[]) =>
    (dispatch: Dispatch<SetStateAction<SelectedState>>) =>
    (selected: Option[]) => {
        dispatch((prev) => ({
            ...reset.reduce(
                (acc, curr) => {
                    acc[curr] = [];
                    return acc;
                },
                { ...prev }
            ),
            [target]: selected,
        }));
    };

/**
 * Convert an array of selected IDs (or `'*'`) to an array of {@link Option}s.
 */
const createOptions = (items: string[], names: NameMap[keyof NameMap]): Option[] => {
    return items.map((id) => ({
        id,
        name: id === '*' ? 'All' : names[id],
    }));
};

/**
 * Generate an initial {@link SelectedState} from claim selection strings
 */
export const initOptions = (populate: { [K in keyof SelectedState]?: string }, nameMap: NameMap) => {
    return (Object.entries(populate) as [keyof SelectedState, string][]).reduce((acc, [type, val]) => {
        acc[type] = createOptions(val?.split(',') || [], nameMap![type]);
        return acc;
    }, {} as Partial<SelectedState>);
};
